﻿import React, { useState, useEffect, useCallback } from 'react';
import { FaTh, FaPlus, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import { apiCall } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useStateContext } from '../../../contexts/ContextProvider';
import type { SessionType, FacilityType, RoomType } from '../../../types';

const SessionTypes: React.FC = () => {
        const { token } = useAuth();
        // Session types are scoped to the top-nav facility selector — there's
        // no in-page facility picker, this page always manages the currently
        // selected facility's own session types.
        const { selectedFacilityId, selectedFacilityName } = useStateContext();
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [sessionTypes, setSessionTypes] = useState<SessionType[]>([]);
        const [facilityTypes, setFacilityTypes] = useState<FacilityType[]>([]);
        const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
        const [loading, setLoading] = useState(true);
        const [saving, setSaving] = useState(false);
        const [error, setError] = useState<string | null>(null);
        
        const [currentSession, setCurrentSession] = useState<{
                id: number | null;
                name: string;
                description: string;
                color: string;
                is_active: boolean;
                is_procedure_type: boolean;
                facility_types: number[];
                allowed_room_types: number[];
        }>({
                id: null,
                name: '',
                description: '',
                color: '#bfdbfe',
                is_active: true,
                is_procedure_type: false,
                facility_types: [],
                allowed_room_types: []
        });
        
        const [searchTerm, setSearchTerm] = useState('');

        const readErrorMessage = async (response: Response): Promise<string> => {
                const contentType = response.headers.get('content-type') || '';

                if (contentType.includes('application/json')) {
                        try {
                                const data = await response.json();
                                return typeof data === 'string' ? data : JSON.stringify(data);
                        } catch {
                                return `Request failed (${response.status})`;
                        }
                }

                try {
                        const text = await response.text();
                        const cleaned = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                        if (cleaned) {
                                return cleaned.slice(0, 300);
                        }
                } catch {
                        // ignore and fall back below
                }

                return `Request failed (${response.status} ${response.statusText})`;
        };

        // Color options for session types
        const colorOptions = [
                { value: '#bfdbfe', label: 'Blue' },
                { value: '#bbf7d0', label: 'Green' },
                { value: '#fef08a', label: 'Yellow' },
                { value: '#fecaca', label: 'Red' },
                { value: '#ddd6fe', label: 'Purple' },
                { value: '#fed7aa', label: 'Orange' },
                { value: '#fbcfe8', label: 'Pink' },
                { value: '#e5e7eb', label: 'Gray' },
        ];

        // Load session types from API — scoped to the selected facility
        const loadSessionTypes = useCallback(async () => {
                if (!selectedFacilityId) {
                        setSessionTypes([]);
                        return;
                }
                try {
                        const response = await apiCall(`/providers/session-types/?facility=${selectedFacilityId}`, { method: 'GET' }, token || undefined);
                        if (!response.ok) {
                                throw new Error(await readErrorMessage(response));
                        }
                        const data = await response.json();
                        const items = data.results || data;
                        setSessionTypes(Array.isArray(items) ? items : []);
                } catch (err) {
                        console.error('Error loading session types:', err);
                        setError(err instanceof Error ? err.message : 'Failed to load session types');
                }
        }, [token, selectedFacilityId]);

        // Load facility types from API
        const loadFacilityTypes = useCallback(async () => {
                try {
                        const response = await apiCall('/facilities/facility-types/', { method: 'GET' }, token || undefined);
                        if (!response.ok) {
                                throw new Error(await readErrorMessage(response));
                        }
                        const data = await response.json();
                        const items = data.results || data;
                        setFacilityTypes(Array.isArray(items) ? items : []);
                } catch (err) {
                        console.error('Error loading facility types:', err);
                }
        }, [token]);

        // Load room types from API
        const loadRoomTypes = useCallback(async () => {
                try {
                        const response = await apiCall('/facilities/room-types/', { method: 'GET' }, token || undefined);
                        if (!response.ok) {
                                throw new Error(await readErrorMessage(response));
                        }
                        const data = await response.json();
                        const items = data.results || data;
                        setRoomTypes(Array.isArray(items) ? items : []);
                } catch (err) {
                        console.error('Error loading room types:', err);
                }
        }, [token]);

        // Load all data on mount
        useEffect(() => {
                const initializeData = async () => {
                        setLoading(true);
                        await Promise.all([loadSessionTypes(), loadFacilityTypes(), loadRoomTypes()]);
                        setLoading(false);
                };
                initializeData();
        }, [loadSessionTypes, loadFacilityTypes, loadRoomTypes]);

        const handleSubmit = async (e: React.FormEvent) => {
                e.preventDefault();
                if (!selectedFacilityId) {
                        setError('Select a facility before adding a session type.');
                        return;
                }
                setSaving(true);
                setError(null);

                try {
                        const method = currentSession.id ? 'PATCH' : 'POST';
                        const url = currentSession.id
                                ? `/providers/session-types/${currentSession.id}/`
                                : '/providers/session-types/';

                        const response = await apiCall(url, {
                                method,
                                // facility is only meaningful on create — PATCH never moves a
                                // session type to a different facility.
                                body: JSON.stringify(
                                        currentSession.id ? currentSession : { ...currentSession, facility: selectedFacilityId }
                                )
                        }, token || undefined);

                        if (response.ok) {
                                await loadSessionTypes();
                                setIsModalOpen(false);
                                resetForm();
                        } else {
                                setError(await readErrorMessage(response));
                        }
                } catch (err) {
                        console.error('Error saving session type:', err);
                        setError(err instanceof Error ? err.message : 'Failed to save session type');
                } finally {
                        setSaving(false);
                }
        };

        const handleEdit = (session: SessionType) => {
                setCurrentSession({
                        id: session.id,
                        name: session.name,
                        description: session.description || '',
                        color: session.color || '#bfdbfe',
                        is_active: session.is_active,
                        is_procedure_type: session.is_procedure_type || false,
                        facility_types: session.facility_types || [],
                        allowed_room_types: session.allowed_room_types || []
                });
                setIsModalOpen(true);
        };

        const handleDelete = async (id: number) => {
                if (!window.confirm('Are you sure you want to delete this session type?')) return;

                try {
                        await apiCall(`/providers/session-types/${id}/`, {
                                method: 'DELETE'
                        }, token || undefined);
                        await loadSessionTypes();
                } catch (err) {
                        console.error('Error deleting session type:', err);
                        setError(err instanceof Error ? err.message : 'Failed to delete session type');
                }
        };

        const resetForm = () => {
                setCurrentSession({
                        id: null,
                        name: '',
                        description: '',
                        color: '#bfdbfe',
                        is_active: true,
                        is_procedure_type: false,
                        facility_types: [],
                        allowed_room_types: []
                });
        };

        const toggleFacilityType = (facilityTypeId: number) => {
                setCurrentSession(prev => ({
                        ...prev,
                        facility_types: prev.facility_types.includes(facilityTypeId)
                                ? prev.facility_types.filter(id => id !== facilityTypeId)
                                : [...prev.facility_types, facilityTypeId]
                }));
        };

        const toggleRoomType = (roomTypeId: number) => {
                setCurrentSession(prev => ({
                        ...prev,
                        allowed_room_types: prev.allowed_room_types.includes(roomTypeId)
                                ? prev.allowed_room_types.filter(id => id !== roomTypeId)
                                : [...prev.allowed_room_types, roomTypeId]
                }));
        };

        // Filter sessions based on search term
        const filteredSessions = sessionTypes.filter(session =>
                session.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                session.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (loading) {
                return (
                        <div className="p-6 flex items-center justify-center">
                                <span className="loading loading-spinner loading-lg"></span>
                        </div>
                );
        }

        return (
                <div className="p-6">
                        {/* Error Display */}
                        {error && (
                                <div className="alert alert-error mb-4">
                                        <span>{error}</span>
                                        <button onClick={() => setError(null)} className="btn btn-sm btn-ghost">
                                                <FaTimes />
                                        </button>
                                </div>
                        )}

                        {/* Header Section */}
                        <div className="p-6">
                                <div className="flex justify-between items-center mb-1">
                                        <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                                                <FaTh className="text-primary" />
                                                Session Types
                                        </h1>
                                        <button
                                                className="btn btn-primary"
                                                disabled={!selectedFacilityId}
                                                onClick={() => {
                                                        resetForm();
                                                        setIsModalOpen(true);
                                                }}
                                        >
                                                <FaPlus className="mr-2" /> Add Session Type
                                        </button>
                                </div>
                                <p className="text-sm opacity-60 mb-5">
                                        {selectedFacilityId
                                                ? `Session types are specific to ${selectedFacilityName || 'this facility'} — not shared with any other facility.`
                                                : 'Select a facility from the top nav to view or manage its session types.'}
                                </p>

                                {/* Search Bar */}
                                <div className="mb-6">
                                        <input
                                                type="text"
                                                placeholder="Search session types..."
                                                className="input input-bordered w-full max-w-md"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                </div>

                                {/* Session Types Table */}
                                <div className="bg-base-100 rounded-lg shadow">
                                        <div className="overflow-x-auto">
                                                <table className="table table-zebra w-full">
                                                        <thead>
                                                                <tr>
                                                                        <th>Name</th>
                                                                        <th>Description</th>
                                                                        <th>Color</th>
                                                                        <th>Procedure Type</th>
                                                                        <th>Facility Types</th>
                                                                        <th>Room Types</th>
                                                                        <th>Status</th>
                                                                        <th>Actions</th>
                                                                </tr>
                                                        </thead>
                                                        <tbody>
                                                                {filteredSessions.map((session) => (
                                                                        <tr key={session.id}>
                                                                                <td className="font-medium">{session.name}</td>
                                                                                <td className="text-sm">{session.description || '-'}</td>
                                                                                <td>
                                                                                        <div className="w-16 h-8 rounded border border-base-300" style={{ backgroundColor: session.color || '#bfdbfe' }}></div>
                                                                                </td>
                                                                                <td>
                                                                                        {session.is_procedure_type ? (
                                                                                                <FaCheck className="text-success" />
                                                                                        ) : (
                                                                                                <FaTimes className="text-error" />
                                                                                        )}
                                                                                </td>
                                                                                <td className="text-xs">
                                                                                        {session.facility_type_details && session.facility_type_details.length > 0
                                                                                                ? session.facility_type_details.map(ft => ft.name).join(', ')
                                                                                                : 'Any'}
                                                                                </td>
                                                                                <td className="text-xs">
                                                                                        {session.room_type_details && session.room_type_details.length > 0
                                                                                                ? session.room_type_details.map(rt => rt.name).join(', ')
                                                                                                : 'Any'}
                                                                                </td>
                                                                                <td>
                                                                                        <span className={`badge ${session.is_active ? 'badge-success' : 'badge-error'}`}>
                                                                                                {session.is_active ? 'Active' : 'Inactive'}
                                                                                        </span>
                                                                                </td>
                                                                                <td>
                                                                                        <div className="flex gap-2">
                                                                                                <button
                                                                                                        className="btn btn-warning btn-xs"
                                                                                                        onClick={() => handleEdit(session)}
                                                                                                        title="Edit Session Type"
                                                                                                >
                                                                                                        <FaEdit className="w-3 h-3" />
                                                                                                </button>
                                                                                                <button
                                                                                                        className="btn btn-error btn-xs"
                                                                                                        onClick={() => handleDelete(session.id)}
                                                                                                        title="Delete Session Type"
                                                                                                >
                                                                                                        <FaTrash className="w-3 h-3" />
                                                                                                </button>
                                                                                        </div>
                                                                                </td>
                                                                        </tr>
                                                                ))}
                                                        </tbody>
                                                </table>
                                        </div>
                                </div>
                        </div>

                        {/* Add/Edit Modal */}
                        {isModalOpen && (
                                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                        <div className="bg-base-100 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                                                <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-base-100 z-10">
                                                        <h3 className="text-xl font-semibold">
                                                                {currentSession.id ? 'Edit Session Type' : 'Add Session Type'}
                                                        </h3>
                                                        <button
                                                                className="btn btn-ghost btn-sm"
                                                                onClick={() => setIsModalOpen(false)}
                                                                disabled={saving}
                                                        >
                                                                ×
                                                        </button>
                                                </div>

                                                <form onSubmit={handleSubmit} className="p-4">
                                                        <div className="space-y-4">
                                                                {/* Basic Information */}
                                                                <div className="grid grid-cols-1 gap-4">
                                                                        <div>
                                                                                <label className="label">
                                                                                        <span className="label-text">Session Type Name *</span>
                                                                                </label>
                                                                                <input
                                                                                        type="text"
                                                                                        className="input input-bordered w-full"
                                                                                        value={currentSession.name}
                                                                                        onChange={(e) => setCurrentSession({
                                                                                                ...currentSession,
                                                                                                name: e.target.value
                                                                                        })}
                                                                                        required
                                                                                />
                                                                        </div>
                                                                </div>

                                                                <div>
                                                                        <label className="label">
                                                                                <span className="label-text">Description</span>
                                                                        </label>
                                                                        <textarea
                                                                                className="textarea textarea-bordered w-full"
                                                                                value={currentSession.description}
                                                                                onChange={(e) => setCurrentSession({
                                                                                        ...currentSession,
                                                                                        description: e.target.value
                                                                                })}
                                                                                rows={3}
                                                                        />
                                                                </div>

                                                                {/* Color Selection */}
                                                                <div>
                                                                        <label className="label">
                                                                                <span className="label-text">Color</span>
                                                                        </label>
                                                                        <div className="grid grid-cols-4 gap-2">
                                                                                {colorOptions.map(color => (
                                                                                        <button
                                                                                                key={color.value}
                                                                                                type="button"
                                                                                                className={`p-4 rounded border-2 ${currentSession.color === color.value ? 'border-primary' : 'border-transparent'}`}
                                                                                                style={{ backgroundColor: color.value }}
                                                                                                onClick={() => setCurrentSession({
                                                                                                        ...currentSession,
                                                                                                        color: color.value
                                                                                                })}
                                                                                        >
                                                                                                {color.label}
                                                                                        </button>
                                                                                ))}
                                                                        </div>
                                                                </div>

                                                                {/* Checkboxes */}
                                                                <div className="form-control">
                                                                        <label className="label cursor-pointer">
                                                                                <span className="label-text">Is this a procedure/operating session type?</span>
                                                                                <input
                                                                                        type="checkbox"
                                                                                        className="checkbox checkbox-primary"
                                                                                        checked={currentSession.is_procedure_type}
                                                                                        onChange={(e) => setCurrentSession({
                                                                                                ...currentSession,
                                                                                                is_procedure_type: e.target.checked
                                                                                        })}
                                                                                />
                                                                        </label>
                                                                </div>

                                                                <div className="form-control">
                                                                        <label className="label cursor-pointer">
                                                                                <span className="label-text">Active</span>
                                                                                <input
                                                                                        type="checkbox"
                                                                                        className="checkbox checkbox-primary"
                                                                                        checked={currentSession.is_active}
                                                                                        onChange={(e) => setCurrentSession({
                                                                                                ...currentSession,
                                                                                                is_active: e.target.checked
                                                                                        })}
                                                                                />
                                                                        </label>
                                                                </div>

                                                                {/* Facility Types */}
                                                                <div>
                                                                        <label className="label">
                                                                                <span className="label-text font-semibold">Allowed Facility Types</span>
                                                                                <span className="label-text-alt text-xs">Leave empty to allow all</span>
                                                                        </label>
                                                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4 border rounded">
                                                                                {facilityTypes.map(ft => (
                                                                                        <div key={ft.id} className="form-control">
                                                                                                <label className="label cursor-pointer justify-start gap-2">
                                                                                                        <input
                                                                                                                type="checkbox"
                                                                                                                className="checkbox checkbox-sm"
                                                                                                                checked={currentSession.facility_types.includes(ft.id)}
                                                                                                                onChange={() => toggleFacilityType(ft.id)}
                                                                                                        />
                                                                                                        <span className="label-text">{ft.name}</span>
                                                                                                </label>
                                                                                        </div>
                                                                                ))}
                                                                        </div>
                                                                </div>

                                                                {/* Room Types */}
                                                                <div>
                                                                        <label className="label">
                                                                                <span className="label-text font-semibold">Allowed Room Types</span>
                                                                                <span className="label-text-alt text-xs">Leave empty to allow all</span>
                                                                        </label>
                                                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 p-4 border rounded max-h-64 overflow-y-auto">
                                                                                {roomTypes.map(rt => (
                                                                                        <div key={rt.id} className="form-control">
                                                                                                <label className="label cursor-pointer justify-start gap-2">
                                                                                                        <input
                                                                                                                type="checkbox"
                                                                                                                className="checkbox checkbox-sm"
                                                                                                                checked={currentSession.allowed_room_types.includes(rt.id)}
                                                                                                                onChange={() => toggleRoomType(rt.id)}
                                                                                                        />
                                                                                                        <span className="label-text text-xs">{rt.name}</span>
                                                                                                </label>
                                                                                        </div>
                                                                                ))}
                                                                        </div>
                                                                </div>
                                                        </div>

                                                        <div className="flex justify-end gap-2 mt-6">
                                                                <button
                                                                        type="button"
                                                                        className="btn btn-ghost"
                                                                        onClick={() => setIsModalOpen(false)}
                                                                        disabled={saving}
                                                                >
                                                                        Cancel
                                                                </button>
                                                                <button
                                                                        type="submit"
                                                                        className="btn btn-primary"
                                                                        disabled={saving}
                                                                >
                                                                        {saving ? (
                                                                                <>
                                                                                        <span className="loading loading-spinner loading-sm"></span>
                                                                                        Saving...
                                                                                </>
                                                                        ) : (
                                                                                'Save'
                                                                        )}
                                                                </button>
                                                        </div>
                                                </form>
                                        </div>
                                </div>
                        )}
                </div>
        );
};

export default SessionTypes;
